import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { AUTH_TYPE_KEY } from '@brightdrop/auth-client';
import { BDSpinner } from '@brightdrop/bd-ui';
import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { AlertMessage } from '~/common/components';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { LOGIN_ATTEMPT_TIMEOUT } from '~/common/configs/auth.config';
import usePageTitle from '~/common/hooks/usePageTitle';
import {
  LOCALIZATION_FEATURE_FLAG,
  LOGIN_UPDATE_FLAG,
} from '~/common/models/featureFlags.model';
import { AppRoutePaths } from '~/common/models/route.model';
import { getActiveLocale } from '~/common/utils/locale.utils';

import {
  AuthenticationType,
  type AuthType,
} from '~/models/authentication.model';
import EmailLoginForm from './EmailLoginForm';
import useStyles from './Login.styles';
import useLoginLocale from './useLoginLocale';
import useLoginRedirect from './useLoginRedirect';

const Login = (): JSX.Element => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const { classes } = useStyles();
  const { getFlag } = useFeatureFlags();
  // Subscribe to i18n so `translateText(...)` calls below re-render on locale changes.
  useTranslation(['common']);

  const navigate = useNavigate();
  const { search, hash } = useLocation();
  const searchParams = new URLSearchParams(search);

  usePageTitle(translateText('common:login'));

  const isLoginUpdateEnabled = !!getFlag(LOGIN_UPDATE_FLAG);
  const isLocalizationEnabled = !!getFlag(LOCALIZATION_FEATURE_FLAG);
  const account = instance?.getActiveAccount();

  let authenticationType = searchParams.get(AUTH_TYPE_KEY) as AuthType | null;
  if (!authenticationType) {
    authenticationType = AuthenticationType.CDI;
  }
  const displayCdiInterface = authenticationType === AuthenticationType.CDI;
  const redirectPath = searchParams.get('redirectPath');

  const [showMsg, setShowMsg] = useState(false);
  const [loginLocale, setLoginLocale] = useLoginLocale(isLocalizationEnabled);
  const b2cLocale = isLocalizationEnabled ? loginLocale : getActiveLocale();
  const {
    formSubmitted,
    clearFormSubmitted,
    handleSubmit,
    handleCreateAccount,
  } = useLoginRedirect(redirectPath, authenticationType, b2cLocale);

  const isAccountReady = inProgress === InteractionStatus.None;

  const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
  const hasPendingAuthResponse =
    hashParams.has('code') || hashParams.has('error');

  /** The identity provider sent back an authorization response MSAL is still exchanging. */
  const isProcessingAuthResponse = hasPendingAuthResponse && !isAccountReady;
  /** An account sits in the cache. MSAL treats that as signed-in. */
  const hasCachedAccount = !!account;

  /*
   * The page is only a launcher for the identity provider redirect, so it stays
   * interactive while a redirect is pending. A cached account blocks because
   * MSAL treats any cached account as signed-in (useIsAuthenticated is
   * accounts.length > 0, false only during Startup) and will send the user
   * into the app once Startup finishes.
   */
  const showSpinner = isLoginUpdateEnabled
    ? isProcessingAuthResponse || hasCachedAccount
    : hasCachedAccount || formSubmitted || isProcessingAuthResponse;

  /*
   * A back/forward-cache restore resumes the frozen document, so MSAL's
   * interaction-in-progress flag outlives the redirect that left this page.
   * handleRedirectPromise is memoized per instance on the empty hash key, and
   * MsalProvider already consumed that key at boot, so a second call cannot
   * clear the flag. Reload to construct a new instance.
   */
  useEffect(() => {
    if (!isLoginUpdateEnabled) {
      return;
    }

    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return;
      }

      window.location.reload();
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [isLoginUpdateEnabled]);

  /* set flag to display long-loading message if redirect does not happen quickly */
  useEffect(() => {
    if (isLoginUpdateEnabled || !formSubmitted) {
      return;
    }

    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setShowMsg(true);
      timeoutRef.current = undefined;
    }, LOGIN_ATTEMPT_TIMEOUT);

    return () => {
      clearTimeout(timeoutRef.current);
      clearFormSubmitted();
    };
  }, [clearFormSubmitted, formSubmitted, isLoginUpdateEnabled]);

  useEffect(() => {
    // user has an active valid session, redirect
    if (account && isAccountReady && isAuthenticated) {
      navigate({ pathname: AppRoutePaths.ROOT, search }, { replace: true });
    }
  }, [account, isAccountReady, isAuthenticated, navigate, search]);

  return (
    <section className={classes.loginWrapper}>
      {showSpinner ? (
        <Box className={classes.spinnerContainer} data-testid="loading">
          <BDSpinner />
          {showMsg && (
            <AlertMessage
              body={translateText(
                'common:alertMsg.messageView.login.loginInProgress.timeOut'
              )}
              linkBtn={translateText(
                'common:alertMsg.messageView.login.loginInProgress.tryAgain'
              )}
              onClick={() => navigate(AppRoutePaths.LOGOUT)}
              topic="loginAlert"
              id="login-in-progress"
            />
          )}
        </Box>
      ) : (
        <Box data-testid={`login-form-${authenticationType}`} height={1}>
          <EmailLoginForm
            onSubmit={handleSubmit}
            onCreateAccount={handleCreateAccount}
            displayCdiInterface={displayCdiInterface}
            authenticationType={authenticationType}
            isSubmitting={isLoginUpdateEnabled && formSubmitted}
            locale={loginLocale}
            onLocaleChange={isLocalizationEnabled ? setLoginLocale : undefined}
          />
        </Box>
      )}
    </section>
  );
};

export default Login;
